import { DoubleLeftOutlined, MenuOutlined } from "@ant-design/icons"
import { Button, Flex, Layout, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import Logo from "../../Images/logos/logo.svg?react"
import DevSettingsController from "../../components/DevSettingsController.tsx"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector"
import { toggleMobileMenu, toggleSideBar } from "../../redux/nav/nav.slice.ts"
import { cn } from "../../utils/cn.ts"
import { BranchSelector } from "../Branches/components/BranchSelector.tsx"
import { GlobalSearch } from "../GlobalSearch/GlobalSearch.tsx"
import NotificationsController from "../Notifications/components/NotificationsController.tsx"
import HeaderUserMenu from "../User/HeaderUserMenu.tsx"
import { TimezoneDisplay } from "./TimezoneDisplay.tsx"

const { Header: AntHeader } = Layout

const CollapseToggle = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isSidebarOpen = useAppSelector((state) => state.nav.isSidebarOpen)
  const label = isSidebarOpen ? t("shell.nav.collapse") : t("shell.nav.expand")

  return (
    <Tooltip title={label}>
      <Button
        type="text"
        size="small"
        aria-label={label}
        aria-expanded={isSidebarOpen}
        onClick={() => dispatch(toggleSideBar())}
        className="hidden size-7 shrink-0 text-slate-400 hover:text-slate-600 md:inline-flex"
        icon={
          <DoubleLeftOutlined
            className={cn(
              "text-xs transition-transform duration-300",
              !isSidebarOpen && "rotate-180"
            )}
          />
        }
      />
    </Tooltip>
  )
}

export const Header = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const isMobile = useAppSelector((state) => state.nav.isMobile)
  const isSidebarOpen = useAppSelector((state) => state.nav.isSidebarOpen)
  const timezone = useAppSelector((state) => state.auth.userInfo?.timezone)

  return (
    <AntHeader
      className={
        "top-0 z-20 sticky flex h-[var(--header-height)] items-center gap-3 border-b border-[#E2E8F0] bg-white p-0 pr-3 leading-none"
      }
    >
      {/* Brand zone, the exact width of the rail below it in both states. The
          collapse control moves out of it once collapsed, so it never lands on
          top of the mark in 64px. */}
      <Flex
        align="center"
        justify={isSidebarOpen ? "space-between" : "center"}
        gap={8}
        className={cn(
          "h-full shrink-0 px-3 transition-[width] duration-200 md:px-4",
          isSidebarOpen
            ? "md:w-[var(--sidebar-width)]"
            : "md:w-[var(--sidebar-width-collapsed)] md:px-0"
        )}
      >
        {/* One asset, two states, each with a viewBox measured off the real
            artwork rather than the file's own box. The asset declares
            `0 0 81.28 33.926` around ink that actually occupies 75.84 x 35.5,
            so the lockup carried dead space on its right and bottom — the
            container grew, the logo did not. The wordmark is two <text> nodes
            sitting on top of the mark's paths (they start at x=13.6, the paths
            run to x=29.34), so no horizontal crop isolates the symbol: the
            collapsed rail hides the text and tightens onto the paths instead.
            Width and height are both given because the file's own width/height
            attributes would otherwise keep their 81:35 ratio and letterbox the
            art inside it. */}
        <Logo
          className={cn(
            "shrink-0 object-contain",
            // Capped by height, not width: the ink is 75.84 x 35.5 (2.136:1).
            // The 68px header is border-box with a 1px bottom border, so its
            // content box is 67px — 47px is the tallest that still clears 10px
            // above and below, and the ratio then fixes the width at 100.4px.
            // The collapsed symbol is deliberately untouched.
            isSidebarOpen
              ? "h-[47px] w-[100.4px]"
              : "h-[44px] w-[38px] [&_text]:hidden"
          )}
          viewBox={isSidebarOpen ? "0 0 75.84 35.5" : "0 0 29.34 34"}
          aria-label="EZBuild"
        />
        {isSidebarOpen && <CollapseToggle />}
      </Flex>

      {!isSidebarOpen && !isMobile && (
        <div className="hidden md:block">
          <CollapseToggle />
        </div>
      )}

      {!isMobile && <BranchSelector />}

      {/* Bounded, so a wide screen never stretches the field across the gap. */}
      {!isMobile && (
        <div className="hidden min-w-0 max-w-[400px] flex-1 lg:block">
          <GlobalSearch className="w-full" />
        </div>
      )}

      <Flex
        align="center"
        gap={4}
        className="ezb-header-utilities ml-auto shrink-0"
      >
        {timezone && !isMobile && (
          <span className="hidden items-center xl:inline-flex">
            <TimezoneDisplay timezone={timezone} variant="simple" />
          </span>
        )}

        <NotificationsController />

        {import.meta.env.VITE_APP_SHOW_DEV_PANEL && <DevSettingsController />}

        <HeaderUserMenu />

        <Button
          type="text"
          icon={<MenuOutlined />}
          aria-label={t("shell.nav.openMenu")}
          onClick={() => dispatch(toggleMobileMenu())}
          className="md:hidden"
          size="large"
        />
      </Flex>
    </AntHeader>
  )
}
